/**
 * SoleadLab 2.0 - Modal Component
 *
 * Accessible modal dialog with backdrop, animations,
 * and keyboard navigation support.
 */

import { useEffect, useCallback, ReactNode, MouseEvent, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Modal content */
  children: ReactNode;
  /** Size of the modal */
  size?: ModalSize;
  /** Show close button in header */
  showCloseButton?: boolean;
  /** Close when clicking backdrop */
  closeOnBackdrop?: boolean;
  /** Close when pressing Escape */
  closeOnEscape?: boolean;
  /** Footer content (buttons, etc.) */
  footer?: ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Modal dialog component
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  footer,
  className = '',
}: ModalProps) {
  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add/remove event listeners and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, handleKeyDown]);

  // Don't render if not open
  if (!isOpen) return null;

  const modalContent = (
    <div
      className="sl-modal__backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className={`sl-modal sl-modal--${size} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="sl-modal__header">
            {title && (
              <h2 id="modal-title" className="sl-modal__title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className="sl-modal__close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 5l10 10M15 5L5 15" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="sl-modal__body">{children}</div>

        {/* Footer */}
        {footer && <div className="sl-modal__footer">{footer}</div>}
      </div>
    </div>
  );

  // Render in portal to ensure proper stacking
  return createPortal(modalContent, document.body);
}
